/**
 * Returns compacted rows without empty rows between other rows.
 *
 * This is most useful when sending data to the server, not
 * when rendering the UI
 *
 * @param aggRows
 * @param sortedListColumns
 * @returns {*[]}
 */
function compactedRows(aggRows, sortedListColumns) {
  // extract just the rows (ordered by row number)
  const rowObjs = Object.values(aggRows).sort((a, b) => a.row_number - b.row_number)

  // map the value objects into arrays using the precedence of the provided list columns
  return rowObjs.map(rowObj => {
    return sortedListColumns.map(lc => rowObj.values[lc.id])
  })
}

/**
 * Returns sparse rows according to the row_numbers.
 *
 * This is most helpful when rendering the UI, as we don't
 * want to move rows around while users are editing/changing
 * things.  We want to preserve the rows they were added at.
 *
 * @param aggRows
 * @param sortedListColumns
 * @returns {*[]}
 */
function sparseRows(aggRows, sortedListColumns) {
  // extract the max row number
  const maxRowNumber = Object.values(aggRows).reduce(
    (memo, row) => Math.max(memo, row.rowNumber || -1),
    -1,
  )

  // maintain the correct row numbers by placing them into a sparse array
  return Array(maxRowNumber + 1 || 1)
    .fill(0)
    .map((_, idx) => {
      const rowObj = aggRows[idx] || { row_number: idx, values: {} }
      return sortedListColumns.map(lc => rowObj.values[lc.id])
    })
}

/**
 * @returns {(string | undefined)[][]}
 */
export const toDataRowsFromListColumns = (listColumns = [], compacted = false) => {
  const sortedListColumns = [...(listColumns || [])].sort((a, b) => a.precedence - b.precedence)

  /*
      aggregate the rows into the format:
      {
        <row_number> : {
          rowNumber: <row_number>
          values: {
            <list_column_id> : <string value>,
            <list_column_id> : <string value>,
          }
        }
      }
     */
  const aggRows = sortedListColumns.reduce((memo, column) => {
    column.list_values?.forEach(listValue => {
      if (!memo[listValue.row_number]) {
        memo[listValue.row_number] = { rowNumber: listValue.row_number, values: {} }
      }
      memo[listValue.row_number].values[column.id] = listValue.value
    })
    return memo
  }, {})

  return compacted
    ? compactedRows(aggRows, sortedListColumns)
    : sparseRows(aggRows, sortedListColumns)
}
